import { isNil } from 'lodash';
import { isNumeric } from './lodashExtensions';

export const toNumber = (value: number | string): number => {
  if (!isNumeric(value)) {
    throw new Error(`Can't convert non-numeric value ${value} to number`);
  } else {
    return Number(value);
  }
};

/**
 * Returns null or a number, e.g. `5.1234`
 */
export const toNumberOrNull = (value: number | string | null | undefined): number | null => {
  if (isNil(value)) {
    return null;
  } else {
    return toNumber(value);
  }
};

/**
 * Returns null or a numeric string, e.g. `"5.1234"`
 */
export const toNumericStringOrNull = (value: number | string | null): string | null => {
  return toNumberOrNull(value)?.toString() ?? null;
};

export const toCents = (value: number | string): number => {
  return Number((toNumber(value) * 100).toFixed(0));
};

export const toCentsOrNull = (value: number | string | null | undefined): number | null => {
  const numberOrNull = toNumberOrNull(value);
  return isNil(numberOrNull) ? null : Number((numberOrNull * 100).toFixed(0));
};

export const toCentsOrUndefined = (value: number | string | null | undefined): number | undefined => {
  const numberOrUndefined = toNumberOrNull(value) ?? undefined;
  return isNil(numberOrUndefined) ? undefined : Number((numberOrUndefined * 100).toFixed(0));
};

export const fromCents = (value: number): string => {
  return (value / 100).toFixed(2);
};

export const fromCentsOrNull = (value: number | string | null): string | null => {
  const numberOrNull = toNumberOrNull(value);
  return isNil(numberOrNull) ? null : fromCents(numberOrNull);
};

export const fromCentsOrUndefined = (value: number | string | undefined): string | undefined => {
  const numberOrUndefined = toNumberOrNull(value) ?? undefined;
  return isNil(numberOrUndefined) ? undefined : fromCents(numberOrUndefined);
};

export type IntRangeString = `${number}` | `..${number}` | `${number}..` | `${number}..${number}`;
export type IntRangeProps =
  | {
      min?: number;
      max: number;
    }
  | {
      min: number;
      max?: number;
    };

class IntRange {
  public min: number | undefined;
  public max: number | undefined;

  constructor({ min, max }: IntRangeProps) {
    if (isNil(min) && isNil(max)) {
      throw Error('invalid range, neither min nor max given');
    }
    if (!isNil(min) && !isNil(max) && min > max) {
      throw Error(`invalid range min (${min}) > max (${max})`);
    }
    this.min = min;
    this.max = max;
  }

  static fromString(rangeString: IntRangeString) {
    const regex = /^(-?\d+)?(\.\.)?(-?\d+)?$/;
    const result = rangeString.match(regex);

    if (result) {
      const [, min, dots, max] = result;
      if (min && !dots && !max) return new IntRange({ min: Number(min), max: Number(min) }); // single exact value
      if (min && dots && max) return new IntRange({ min: Number(min), max: Number(max) });
      if (min && dots && !max) return new IntRange({ min: Number(min) });
      if (!min && dots && max) return new IntRange({ max: Number(max) });
    }

    throw Error(`invalid range string '${rangeString}'`);
  }
}

export { IntRange };
